import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TriangleAlert as AlertTriangle, CircleArrowRight as ArrowRightCircle, Check, CircleCheck as CheckCircle, Download, Loader as Loader2, Package2, Plus, Search, Star, Upload, X } from 'lucide-react';
import { Portal } from '../Portal';
import { decodeHtml } from '../utils';
import type { InstallResult, Plugin, Theme } from '../types';

interface InstallModalProps {
  show: boolean;
  installType: 'plugin' | 'theme';
  installTab: 'search' | 'zip';
  onSetInstallTab: (tab: 'search' | 'zip') => void;
  installSearchQuery: string;
  onSetInstallSearchQuery: (query: string) => void;
  installResults: InstallResult[];
  installSearching: boolean;
  installingSlug: string | null;
  installMessage: string | null;
  installPage: number;
  hasMoreInstall: boolean;
  zipFile: File | null;
  zipUploading: boolean;
  plugins: Plugin[];
  themes: Theme[];
  onClose: () => void;
  onSearch: (query: string, page: number) => void;
  onInstallFromRepo: (slug: string) => void;
  onInstallZip: () => void;
  onSetZipFile: (file: File | null) => void;
}

export const InstallModal: React.FC<InstallModalProps> = ({
  show,
  installType,
  installTab,
  onSetInstallTab,
  installSearchQuery,
  onSetInstallSearchQuery,
  installResults,
  installSearching,
  installingSlug,
  installMessage,
  installPage,
  hasMoreInstall,
  zipFile,
  zipUploading,
  plugins,
  themes,
  onClose,
  onSearch,
  onInstallFromRepo,
  onInstallZip,
  onSetZipFile,
}) => {
  return (
    <AnimatePresence>
      {show && (
        <Portal>
          <div className="fixed inset-0 z-[9999] flex items-start justify-center p-4 pt-16">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={onClose}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              className="relative w-full max-w-3xl bg-card border border-border rounded-2xl shadow-2xl flex flex-col max-h-[80vh] z-50 overflow-hidden text-foreground"
            >
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30 shrink-0">
                <div>
                  <h3 className="text-base font-bold">Install {installType === 'plugin' ? 'Plugin' : 'Theme'}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Search the WordPress repository or upload a ZIP file</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors text-muted-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex border-b border-border bg-muted/20 shrink-0">
                {(['search', 'zip'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => onSetInstallTab(t)}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-all relative ${installTab === t ? 'text-foreground bg-card' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {t === 'search' ? <Search className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                    {t === 'search' ? 'Search Repository' : 'Upload ZIP'}
                    {installTab === t && (
                      <motion.div layoutId="install-tab-ind" className="absolute bottom-0 left-0 right-0 h-0.5 bg-workspace-accent" />
                    )}
                  </button>
                ))}
              </div>

              {installMessage && (
                <div className={`px-5 py-3 text-sm font-medium shrink-0 flex items-center gap-2 ${installMessage.startsWith('Error') ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'}`}>
                  {installMessage.startsWith('Error') ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <CheckCircle className="w-4 h-4 shrink-0" />}
                  {installMessage}
                </div>
              )}

              <div className="flex-1 overflow-y-auto">
                {installTab === 'search' ? (
                  <div className="p-5 space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        autoFocus
                        type="text"
                        value={installSearchQuery}
                        onChange={(e) => onSetInstallSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onSearch(installSearchQuery, 1);
                          }
                        }}
                        placeholder={`Search WordPress.org ${installType} repository (Press Enter)…`}
                        className="w-full pl-10 pr-4 py-2.5 bg-muted/40 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent/30 focus:border-workspace-accent transition-colors"
                      />
                      {installSearching && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                    </div>

                    {installResults.length === 0 && !installSearching && (
                      <div className="text-center py-12">
                        <Package2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">No {installType}s found matching your search</p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {installResults.map((item, idx) => (
                        <div key={`${idx}-${item.slug}`} className="flex items-center gap-4 p-3.5 bg-muted/20 border border-border rounded-xl hover:border-workspace-accent/30 transition-all group">
                          {(item.icon || item.screenshot_url) ? (
                            <img
                              src={item.icon || item.screenshot_url}
                              alt={item.name}
                              className="w-11 h-11 rounded-lg object-cover shrink-0 bg-muted border border-border"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-lg bg-muted border border-border flex items-center justify-center shrink-0">
                              <Package2 className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold truncate">{decodeHtml(item.name)}</p>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">v{item.version}</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{decodeHtml(item.short_description || item.description)}</p>
                            <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                              {item.rating > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                                  {(item.rating / 20).toFixed(1)}
                                </span>
                              )}
                              {(item.active_installs || item.num_downloads) && (
                                <span className="flex items-center gap-0.5">
                                  <Download className="w-2.5 h-2.5" />
                                  {((item.active_installs || item.num_downloads)! >= 1000000)
                                    ? `${Math.floor((item.active_installs || item.num_downloads)! / 1000000)}M+`
                                    : ((item.active_installs || item.num_downloads)! >= 1000)
                                      ? `${Math.floor((item.active_installs || item.num_downloads)! / 1000)}K+`
                                      : item.active_installs || item.num_downloads}
                                </span>
                              )}
                              <span className="text-muted-foreground/70 truncate">by {item.author}</span>
                            </div>
                          </div>

                          {(() => {
                            const isInstalled = installType === 'plugin'
                              ? plugins.some(p => p.plugin_file.startsWith(`${item.slug}/`) || p.plugin_file === `${item.slug}.php`)
                              : themes.some(t => t.stylesheet === item.slug);

                            return (
                              <button
                                onClick={() => !isInstalled && onInstallFromRepo(item.slug)}
                                disabled={installingSlug === item.slug || isInstalled}
                                className={`shrink-0 h-8 px-3 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm
                                  ${isInstalled
                                    ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 cursor-default'
                                    : 'bg-workspace-accent hover:bg-workspace-accent-hover text-white disabled:opacity-60'}`}
                              >
                                {installingSlug === item.slug ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : isInstalled ? (
                                  <Check className="w-3.5 h-3.5" />
                                ) : (
                                  <Plus className="w-3.5 h-3.5" />
                                )}
                                {installingSlug === item.slug ? 'Installing…' : isInstalled ? 'Installed' : 'Install'}
                              </button>
                            );
                          })()}
                        </div>
                      ))}

                      {hasMoreInstall && installResults.length > 0 && (
                        <div className="pt-2">
                          <button
                            onClick={() => onSearch(installSearchQuery, installPage + 1)}
                            disabled={installSearching}
                            className="w-full py-2.5 border border-dashed border-border rounded-xl text-xs font-medium text-muted-foreground hover:bg-muted/30 hover:text-foreground transition-all flex items-center justify-center gap-2"
                          >
                            {installSearching ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <ArrowRightCircle className="w-3.5 h-3.5" />
                            )}
                            Load More Results
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 flex flex-col items-center gap-5">
                    <label
                      htmlFor="zip-upload"
                      className={`w-full border-2 border-dashed rounded-2xl p-10 flex flex-col items-center gap-3 cursor-pointer transition-all ${zipFile ? 'border-workspace-accent/50 bg-workspace-accent/5' : 'border-border hover:border-workspace-accent/40 hover:bg-muted/30'}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.name.endsWith('.zip')) onSetZipFile(f); }}
                    >
                      <Upload className={"w-10 h-10 " + (zipFile ? "text-workspace-accent" : "text-muted-foreground")} />
                      {zipFile ? (
                        <div className="text-center">
                          <p className="text-sm font-semibold text-foreground">{zipFile.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{(zipFile.size / 1024 / 1024).toFixed(2)} MB — ready to install</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className="text-sm font-semibold text-foreground">Drop ZIP here or click to browse</p>
                          <p className="text-xs text-muted-foreground mt-1">Upload a valid WordPress {installType} ZIP package</p>
                        </div>
                      )}
                      <input id="zip-upload" type="file" accept=".zip" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onSetZipFile(f); }} />
                    </label>

                    {zipFile && (
                      <div className="flex gap-3 w-full">
                        <button onClick={() => onSetZipFile(null)} className="flex-1 h-10 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors">
                          Remove
                        </button>
                        <button
                          onClick={onInstallZip}
                          disabled={zipUploading}
                          className="flex-1 h-10 rounded-xl bg-workspace-accent hover:bg-workspace-accent-hover text-white text-sm font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg"
                        >
                          {zipUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Installing…</> : <><Upload className="w-4 h-4" /> Install from ZIP</>}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </Portal>
      )}
    </AnimatePresence>
  );
};
